import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import useConfig from '@hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from '@api/menu';
import ProfileSection from '@layout/MainLayout/ProfileSection';
import { drawerWidth } from '@shared/constants/theme';
import List from '@mui/material/List';
import NavItem from '@layout/MainLayout/MenuList/NavItem';
import { NavItemType } from '@app/types';
import { useDashboardSelector } from '@hooks/useDashboard';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import useNotification from '@hooks/useNotification';
import Divider from '@mui/material/Divider';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';
import { sidebarBottomItems } from './bottom-items';
import { sendFeedbackMessage } from './send-feedback';
import useToast from '@hooks/useToast';
import {
  getRedpenMenuLink,
  openRedpenFeedback,
  subscribeRedpenMenuLink,
} from '@shared/lib/redpenWidget';
import {
  adminLandingPath,
  canOpenAdminArea,
} from '@pages/SuperAdminPanelPage/access';

// ==============================|| SIDEBAR DRAWER ||============================== //

function Sidebar() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const userData = useAuthSelector((state) => state.userData);
  const { notifications } = useNotification();

  const { menuMaster } = useGetMenuMaster();
  const toggleNotificationOpened = useDashboardSelector(
    (state) => state.toggleNotificationOpened
  );
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const { miniDrawer } = useConfig();

  // ZLAYT-1667: "Send feedback" in the side menu, when an admin switches it on.
  const showFeedback = useSyncExternalStore(
    subscribeRedpenMenuLink,
    getRedpenMenuLink
  );
  // useToast hands out a new function each render; keep the latest in a ref
  // so the menu items are not rebuilt on every render.
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const onFeedbackClick = useCallback(() => {
    openRedpenFeedback().then((result) => {
      const message = sendFeedbackMessage(result);
      if (message) {
        showToastRef.current({ ...message, duration: 8000 });
      }
    });
  }, []);

  const logo = useMemo(
    () => (
      <Box sx={{ display: 'flex', p: 2, pb: 0 }}>
        <LogoSection />
      </Box>
    ),
    []
  );

  const bottomItems: NavItemType[] = useMemo(
    () =>
      sidebarBottomItems({
        unreadCount: notifications.filter(
          (notification) => !notification.is_read
        ).length,
        showAdmin: canOpenAdminArea(userData),
        adminUrl: adminLandingPath(userData),
        onNotificationsClick: toggleNotificationOpened,
        showFeedback,
        onFeedbackClick,
      }),
    [
      notifications,
      userData,
      toggleNotificationOpened,
      showFeedback,
      onFeedbackClick,
    ]
  );

  const drawer = useMemo(() => {
    let drawerSX = {
      paddingLeft: '0px',
      paddingRight: '0px',
      marginTop: '16px',
    };
    if (drawerOpen)
      drawerSX = {
        paddingLeft: '16px',
        paddingRight: '16px',
        marginTop: '0px',
      };

    return (
      <Box
        sx={{
          flex: '1 1 auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Scrollable menu section */}
        <Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
          {downMD ? (
            <Box sx={drawerSX}>
              <MenuList />
            </Box>
          ) : (
            <Box sx={drawerSX}>
              <MenuList />
            </Box>
          )}
        </Box>
      </Box>
    );
  }, [downMD, drawerOpen]);

  const profile = useMemo(() => <ProfileSection fromSidebar />, []);

  return (
    <Box
      component="nav"
      sx={{
        // Left out of printouts: its drawer added a run of blank pages after
        // a printed report.
        displayPrint: 'none',
        flexShrink: { md: 0 },
        width: {
          xs: 'auto',
          md: drawerOpen ? drawerWidth : 72,
        },
      }}
      aria-label="mailbox folders"
    >
      {downMD || (miniDrawer && drawerOpen) ? (
        <Drawer
          variant={downMD ? 'temporary' : 'persistent'}
          anchor="left"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          sx={{
            '& .MuiDrawer-paper': {
              mt: downMD ? 0 : 11,
              zIndex: 1099,
              width: 260,
              bgcolor: 'background.default',
              color: 'text.primary',
              borderRight: 'none',
            },
          }}
          ModalProps={{ keepMounted: true }}
          color="inherit"
        >
          {downMD && logo}
          {drawer}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {logo}
          {drawer}
          {/* Bottom Items */}
          <Box
            sx={{
              flex: '0 0 auto',
              paddingX: drawerOpen ? '16px' : '0px',
            }}
          >
            <Divider />
            <List>
              {bottomItems.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  level={1}
                  isParents
                  customAction={item.customAction}
                />
              ))}
            </List>
          </Box>
          {/* Profile Section */}
          <Box
            sx={{
              flex: '0 0 auto',
            }}
          >
            {profile}
          </Box>
        </MiniDrawerStyled>
      )}
    </Box>
  );
}

const MemoizedSidebar = memo(Sidebar);
MemoizedSidebar.displayName = 'Sidebar';

export default MemoizedSidebar;
